import React from "react";
import DateConverter from "@heartinz/date_and_time_processor";
import uRLs from "../../../utils/baseUtils";
import { getSubBookingStatusLabel } from "../../../utils/helper";

// The per-sub-booking stage sequence (independent of the primary booking's
// own overall timeline in AdminBookingTimeline — see that file's
// `stepsToRender` filter for split bookings).
const SUB_BOOKING_STEPS = [
  "BATCHPENDING",
  "BATCHCONFIRMED",
  "TRAVELSTARTED",
  "REACHEDLOCATION",
  "MEASUREMENTACCEPTED",
  "WORKSTARTED",
  "GEOIMAGEUPDATED",
  "BOOKINGCOMPLETED",
];

// AMS-80: a Captain's part ends at Completed once the work is done; there is
// no payment step. Parts closed out before that change ended at PAYMENTCOMPLETED.
const completedStep = (status) => (status === "PAYMENTCOMPLETED" ? "BOOKINGCOMPLETED" : status);

function formatTime(isoTime) {
  if (!isoTime) return null;
  try {
    const converted = DateConverter.convertToTimeZone(isoTime, uRLs.TIMEZONE);
    const { date, time } = DateConverter.ISOToDisplayFormat(converted);
    return `${date} ${time || ""}`.trim();
  } catch {
    return null;
  }
}

// A real, timestamped timeline for one Gang Leader's sub-booking, built from
// its own `bookingStatusHistory` (attached server-side by
// listSubBookingsForAdmin). A cancelled/rejected sub-booking shows a banner
// instead of the stepper, matching the primary timeline's own convention.
export default function GangLeaderSubBookingTimeline({ subBooking }) {
  if (!subBooking) return null;

  if (subBooking.lastStatus === "CANCELLED") {
    return (
      <div className="alert alert-secondary py-2 px-3 mb-0 font-13" role="alert">
        This Captain's allocation was rejected/cancelled.
      </div>
    );
  }

  const history = Array.isArray(subBooking.bookingStatusHistory) ? subBooking.bookingStatusHistory : [];
  const historyByStep = history.reduce((acc, row) => {
    const step = completedStep(row.bookingTracking);
    if (!acc[step]) acc[step] = row;
    return acc;
  }, {});
  const currentIndex = SUB_BOOKING_STEPS.indexOf(completedStep(subBooking.lastStatus));
  const isFinished = currentIndex === SUB_BOOKING_STEPS.length - 1;

  return (
    <div className="sub-booking-timeline ps-1">
      {SUB_BOOKING_STEPS.map((step, index) => {
        const isCompleted = currentIndex >= 0 && index <= currentIndex;
        const isCurrent = index === currentIndex && !isFinished;
        const historyEntry = historyByStep[step];
        const isLast = index === SUB_BOOKING_STEPS.length - 1;
        return (
          <div key={step} className="d-flex position-relative">
            <div className="me-2 d-flex flex-column align-items-center">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: 16,
                  height: 16,
                  minWidth: 16,
                  background: isCompleted ? (isCurrent ? "#ffc107" : "#28a745") : "#dee2e6",
                }}
              />
              {!isLast && (
                <div style={{ width: 2, flexGrow: 1, minHeight: 14, background: isCompleted && index < currentIndex ? "#28a745" : "#dee2e6" }} />
              )}
            </div>
            <div className="pb-2">
              <div className={`font-12 ${isCompleted ? "fw-semibold text-dark" : "text-muted"}`}>
                {getSubBookingStatusLabel(step)}
              </div>
              {historyEntry?.time && (
                <div className="font-11 text-muted">{formatTime(historyEntry.time)}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
